import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "wouter";
import { Calendar, CheckCircle2, ClipboardList, FileText, MessageSquare, Plus, Save, Trash2, UserCheck, Users, XCircle } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { useDemoStore } from "@/store/useDemoStore";
import {
  useAttendanceForGroup,
  useCandidatesList,
  useEffectiveUser,
  useElementsForStandard,
  useExamAvailability,
  useExpedientesList,
  useGroup,
  useLmsAvancePct,
  useLmsProgress,
  useMaterialsForGroup,
  useObservationsForCandidate,
  useProgressForExpediente,
  useSessionsForGroup,
  useStandard,
  useUsers,
} from "@/store/hooks";
import type { AttendanceEstado, GroupMaterialTipo } from "@/data/seed";

type TabKey = "asistencia" | "avance" | "material" | "observaciones";

const TABS: { key: TabKey; label: string; icon: typeof ClipboardList }[] = [
  { key: "asistencia", label: "Lista de asistencia", icon: ClipboardList },
  { key: "avance", label: "Avance por candidato", icon: CheckCircle2 },
  { key: "material", label: "Material", icon: FileText },
  { key: "observaciones", label: "Observaciones", icon: MessageSquare },
];

export default function InstructorGroupDetail() {
  const setRole = useDemoStore((s) => s.setRole);
  useEffect(() => setRole("instructor"), [setRole]);
  const params = useParams<{ groupId: string }>();
  const group = useGroup(params.groupId);
  const candidates = useCandidatesList();
  const standard = useStandard(group?.estandar);
  const sessions = useSessionsForGroup(group?.id);
  const expedientes = useExpedientesList();
  const users = useUsers();
  const [tab, setTab] = useState<TabKey>("asistencia");

  const groupCandidates = useMemo(
    () => (group ? candidates.filter((c) => group.candidatosIds.includes(c.id)) : []),
    [group, candidates],
  );

  if (!group) {
    return (
      <AppLayout pageTitle="Grupo no encontrado" breadcrumb={["Instructor", "Grupo"]}>
        <Card><div style={{ padding: 20, fontSize: 14 }}>Grupo no encontrado.</div></Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle={group.nombre} breadcrumb={["Instructor", "Mis grupos", group.nombre]}>
      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: "var(--cc-navy)" }}>{group.nombre}</h1>
          <div style={{ fontSize: 13, color: "var(--cc-gray-500)" }}>
            {standard?.codigo ?? group.estandar} · {standard?.titulo ?? ""} · {groupCandidates.length} candidatos · {group.inicio} → {group.fin}
          </div>
        </div>
        <Badge tone={group.estado === "en-curso" ? "teal" : "gray"}>{group.estado}</Badge>
      </div>

      <div style={{ display: "flex", gap: 4, marginBottom: 14, borderBottom: "1px solid var(--cc-gray-200)" }}>
        {TABS.map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`tab-${t.key}`}
              style={{
                background: "transparent",
                border: "none",
                padding: "10px 14px",
                fontSize: 13,
                fontWeight: 600,
                color: active ? "var(--cc-teal)" : "var(--cc-gray-500)",
                borderBottom: active ? "2px solid var(--cc-teal)" : "2px solid transparent",
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Icon size={14} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "asistencia" && <AsistenciaTab grupoId={group.id} candidatos={groupCandidates} sesiones={sessions} />}
      {tab === "avance" && <AvanceTab grupoId={group.id} candidatos={groupCandidates} estandar={group.estandar} expedientes={expedientes} users={users} />}
      {tab === "material" && <MaterialTab grupoId={group.id} />}
      {tab === "observaciones" && <ObservacionesTab grupoId={group.id} candidatos={groupCandidates} users={users} />}
    </AppLayout>
  );
}

// ---------- Asistencia ----------
function AsistenciaTab({
  grupoId,
  candidatos,
  sesiones,
}: {
  grupoId: string;
  candidatos: ReturnType<typeof useCandidatesList>;
  sesiones: ReturnType<typeof useSessionsForGroup>;
}) {
  const attendance = useAttendanceForGroup(grupoId);
  const setAttendance = useDemoStore((s) => s.setAttendance);
  const ordered = useMemo(
    () => [...sesiones].sort((a, b) => (a.fecha + a.hora).localeCompare(b.fecha + b.hora)),
    [sesiones],
  );
  const [openId, setOpenId] = useState<string | null>(ordered[0]?.id ?? null);
  const session = ordered.find((s) => s.id === openId) ?? ordered[0];

  if (!session) {
    return <Card><div style={{ padding: 16, fontSize: 13, color: "var(--cc-gray-500)" }}>Sin sesiones programadas.</div></Card>;
  }

  const summary = (sid: string) => {
    const m = attendance[sid] ?? {};
    const pres = Object.values(m).filter((x) => x.estado === "presente").length;
    const aus = Object.values(m).filter((x) => x.estado === "ausente").length;
    const tar = Object.values(m).filter((x) => x.estado === "tarde").length;
    return { pres, aus, tar, marked: pres + aus + tar };
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 14 }}>
      <Card padding={0}>
        <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--cc-gray-200)", fontSize: 13, fontWeight: 700, color: "var(--cc-gray-800)" }}>Sesiones</div>
        <div>
          {ordered.map((s) => {
            const { marked } = summary(s.id);
            const active = s.id === openId;
            return (
              <button
                key={s.id}
                onClick={() => setOpenId(s.id)}
                data-testid={`session-pick-${s.id}`}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "10px 14px",
                  background: active ? "var(--cc-teal-bg)" : "transparent",
                  border: "none",
                  borderBottom: "1px solid var(--cc-gray-100)",
                  cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: active ? "var(--cc-teal)" : "var(--cc-navy)" }}>{s.titulo}</span>
                  <span style={{ fontSize: 10, color: "var(--cc-gray-500)" }}>{marked}/{candidatos.length}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--cc-gray-500)", marginTop: 2 }}>
                  <Calendar size={10} style={{ verticalAlign: "middle", marginRight: 4 }} />
                  {s.fecha} · {s.hora}
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      <Card padding={0}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cc-gray-200)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "var(--cc-navy)" }}>{session.titulo}</div>
            <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{session.fecha} · {session.hora} · {session.sede ?? "—"}</div>
          </div>
          <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>
            P: {summary(session.id).pres} · T: {summary(session.id).tar} · A: {summary(session.id).aus}
          </div>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ background: "var(--cc-gray-50)", textAlign: "left" }}>
              {["Candidato", "Estado", "Nota", ""].map((h) => (
                <th key={h} style={{ padding: "10px 14px", fontSize: 11, fontWeight: 600, color: "var(--cc-gray-500)", textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {candidatos.map((c) => {
              const mark = attendance[session.id]?.[c.id];
              return (
                <tr key={c.id} style={{ borderTop: "1px solid var(--cc-gray-100)" }}>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", background: c.avatarColor, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700 }}>{c.iniciales}</div>
                      <span style={{ fontWeight: 600, color: "var(--cc-navy)" }}>{c.nombre}</span>
                    </div>
                  </td>
                  <td style={{ padding: "10px 14px" }}>
                    <div style={{ display: "flex", gap: 6 }}>
                      {(["presente", "tarde", "ausente"] as AttendanceEstado[]).map((est) => {
                        const active = mark?.estado === est;
                        return (
                          <button
                            key={est}
                            onClick={() => setAttendance(grupoId, session.id, c.id, { estado: est, nota: mark?.nota })}
                            data-testid={`mark-${session.id}-${c.id}-${est}`}
                            style={{
                              padding: "4px 10px",
                              borderRadius: 6,
                              border: active ? "none" : "1px solid var(--cc-gray-200)",
                              fontSize: 11,
                              fontWeight: 600,
                              cursor: "pointer",
                              background: active
                                ? est === "presente" ? "var(--cc-green)" : est === "tarde" ? "var(--cc-amber)" : "var(--cc-red)"
                                : "white",
                              color: active ? "white" : "var(--cc-gray-700)",
                              textTransform: "capitalize",
                            }}
                          >
                            {est}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td style={{ padding: "10px 14px", width: 260 }}>
                    <input
                      key={`${session.id}-${c.id}-${mark?.nota ?? ""}`}
                      type="text"
                      defaultValue={mark?.nota ?? ""}
                      placeholder="Nota opcional"
                      onBlur={(e) => {
                        if (!mark) return;
                        const v = e.currentTarget.value.trim();
                        if (v === (mark.nota ?? "")) return;
                        setAttendance(grupoId, session.id, c.id, { estado: mark.estado, nota: v || undefined });
                      }}
                      disabled={!mark}
                      style={{ width: "100%", padding: "6px 8px", border: "1px solid var(--cc-gray-200)", borderRadius: 6, fontSize: 12 }}
                    />
                  </td>
                  <td style={{ padding: "10px 14px", fontSize: 11, color: "var(--cc-gray-500)" }}>
                    {mark?.registradoEn ? new Date(mark.registradoEn).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" }) : "—"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

function CandidateLmsPill({ expedienteId, codigo }: { expedienteId: string | undefined; codigo: string }) {
  const pct = useLmsAvancePct(expedienteId, codigo);
  const progress = useLmsProgress(expedienteId);
  if (!expedienteId) return null;
  const tone = pct >= 80 ? "green" : pct >= 50 ? "teal" : pct > 0 ? "amber" : "red";
  const failedTwicePlus = Object.values(progress.attemptsByModule ?? {}).some(
    (atts) => atts.filter((a) => !a.aprobado).length >= 2,
  );
  const atRisk = pct < 40 || failedTwicePlus;
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center", flexWrap: "wrap" }}>
      <Badge tone={tone as "green" | "teal" | "amber" | "red"}>RF {pct}%</Badge>
      {atRisk && (
        <span data-testid={`lms-risk-${expedienteId}`}>
          <Badge tone="red">⚠ Riesgo</Badge>
        </span>
      )}
    </div>
  );
}

// ---------- Avance ----------
function AvanceTab({
  grupoId,
  candidatos,
  estandar,
  expedientes,
  users,
}: {
  grupoId: string;
  candidatos: ReturnType<typeof useCandidatesList>;
  estandar: string;
  expedientes: ReturnType<typeof useExpedientesList>;
  users: ReturnType<typeof useUsers>;
}) {
  const me = useEffectiveUser();
  const topics = useElementsForStandard(estandar);
  const [openCandId, setOpenCandId] = useState<string | null>(candidatos[0]?.id ?? null);

  if (topics.length === 0) {
    return <Card><div style={{ padding: 16, fontSize: 13, color: "var(--cc-gray-500)" }}>El estándar {estandar} no tiene elementos cargados.</div></Card>;
  }

  return (
    <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: 14 }}>
      <Card padding={0}>
        <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--cc-gray-200)", fontSize: 13, fontWeight: 700, color: "var(--cc-gray-800)" }}>Candidatos · Avance formativo</div>
        <div>
          {candidatos.map((c) => {
            const exp = expedientes.find((e) => e.candidatoId === c.id && e.estandar === estandar);
            return (
              <button
                key={c.id}
                onClick={() => setOpenCandId(c.id)}
                data-testid={`cand-pick-${c.id}`}
                style={{
                  display: "block", width: "100%", textAlign: "left", padding: "10px 14px",
                  background: openCandId === c.id ? "var(--cc-teal-bg)" : "transparent",
                  border: "none", borderBottom: "1px solid var(--cc-gray-100)", cursor: "pointer",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <div style={{ width: 24, height: 24, borderRadius: "50%", background: c.avatarColor, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>{c.iniciales}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>{c.nombre}</div>
                    <div style={{ fontSize: 10, color: "var(--cc-gray-500)" }}>{exp ? exp.id : "Sin expediente"}</div>
                  </div>
                  <CandidateLmsPill expedienteId={exp?.id} codigo={estandar} />
                </div>
              </button>
            );
          })}
        </div>
      </Card>

      {openCandId ? (
        <CandidateProgressCard
          grupoId={grupoId}
          candidatoId={openCandId}
          estandar={estandar}
          topics={topics}
          expedientes={expedientes}
          users={users}
          actorId={me?.id ?? "instructor"}
        />
      ) : (
        <Card><div style={{ padding: 16, fontSize: 13 }}>Seleccione un candidato.</div></Card>
      )}
    </div>
  );
}

function CandidateProgressCard({
  grupoId, candidatoId, estandar, topics, expedientes, users, actorId,
}: {
  grupoId: string;
  candidatoId: string;
  estandar: string;
  topics: ReturnType<typeof useElementsForStandard>;
  expedientes: ReturnType<typeof useExpedientesList>;
  users: ReturnType<typeof useUsers>;
  actorId: string;
}) {
  void grupoId; void users;
  const exp = expedientes.find((e) => e.candidatoId === candidatoId && e.estandar === estandar);
  const progress = useProgressForExpediente(exp?.id);
  const setTopic = useDemoStore((s) => s.setProgressTopic);
  const availability = useExamAvailability(exp?.id);
  const setAvailability = useDemoStore((s) => s.setExamAvailability);

  if (!exp) {
    return <Card><div style={{ padding: 16, fontSize: 13, color: "var(--cc-gray-500)" }}>El candidato no tiene un expediente abierto para {estandar}.</div></Card>;
  }

  const total = topics.length;
  const done = topics.filter((t) => progress[t.id]).length;
  const pct = total ? Math.round((done / total) * 100) : 0;

  return (
    <Card padding={0}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cc-gray-200)" }}>
        <SectionTitle action={<Badge tone={pct === 100 ? "green" : pct >= 50 ? "teal" : "amber"}>{pct}% completado</Badge>}>
          Temario · {done}/{total} temas
        </SectionTitle>
        <div style={{ background: "var(--cc-gray-100)", borderRadius: 999, height: 6, overflow: "hidden" }}>
          <div style={{ background: pct === 100 ? "var(--cc-green)" : "var(--cc-teal)", width: `${pct}%`, height: "100%" }} />
        </div>
      </div>

      <div style={{ padding: 18, display: "flex", flexDirection: "column", gap: 8 }}>
        {topics.map((t) => {
          const checked = !!progress[t.id];
          return (
            <label key={t.id} data-testid={`topic-${exp.id}-${t.id}`} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", border: "1px solid var(--cc-gray-100)", borderRadius: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={checked}
                onChange={(e) => setTopic(exp.id, t.id, e.target.checked)}
                style={{ width: 16, height: 16, accentColor: "var(--cc-teal)" }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{t.codigo} · {t.titulo}</div>
                <div style={{ fontSize: 11, color: "var(--cc-gray-500)" }}>{t.criterios.length} criterios · {t.evidencias.length} evidencias</div>
              </div>
              {checked && <CheckCircle2 size={16} color="var(--cc-green)" />}
            </label>
          );
        })}
      </div>

      <div style={{ padding: 18, borderTop: "1px solid var(--cc-gray-200)", background: "var(--cc-gray-50)" }}>
        <SectionTitle>Habilitación de exámenes</SectionTitle>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Toggle
            label="Diagnóstico habilitado"
            checked={availability.diagnostico}
            onChange={(v) => setAvailability(exp.id, { diagnostico: v }, actorId)}
            testId={`toggle-diag-${exp.id}`}
          />
          <Toggle
            label="Examen teórico habilitado"
            checked={availability.teorico}
            onChange={(v) => setAvailability(exp.id, { teorico: v }, actorId)}
            testId={`toggle-teo-${exp.id}`}
          />
        </div>
        {availability.habilitadoEn && (
          <div style={{ fontSize: 11, color: "var(--cc-gray-500)", marginTop: 8 }}>
            Última actualización: {new Date(availability.habilitadoEn).toLocaleString("es-MX")}
            {availability.habilitadoPor ? ` · ${availability.habilitadoPor}` : ""}
          </div>
        )}
      </div>
    </Card>
  );
}

function Toggle({ label, checked, onChange, testId }: { label: string; checked: boolean; onChange: (v: boolean) => void; testId?: string }) {
  return (
    <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: "white", border: "1px solid var(--cc-gray-200)", borderRadius: 8, cursor: "pointer" }}>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={testId} style={{ width: 16, height: 16, accentColor: "var(--cc-teal)" }} />
      <span style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)" }}>{label}</span>
      {checked ? <CheckCircle2 size={16} color="var(--cc-green)" style={{ marginLeft: "auto" }} /> : <XCircle size={16} color="var(--cc-gray-400)" style={{ marginLeft: "auto" }} />}
    </label>
  );
}

// ---------- Material ----------
function MaterialTab({ grupoId }: { grupoId: string }) {
  const materials = useMaterialsForGroup(grupoId);
  const add = useDemoStore((s) => s.addGroupMaterial);
  const remove = useDemoStore((s) => s.removeGroupMaterial);
  const me = useEffectiveUser();
  const [titulo, setTitulo] = useState("");
  const [url, setUrl] = useState("");
  const [tipo, setTipo] = useState<GroupMaterialTipo>("documento");

  const submit = () => {
    if (!titulo.trim() || !url.trim()) return;
    add(grupoId, { titulo: titulo.trim(), url: url.trim(), tipo, autorId: me?.id });
    setTitulo(""); setUrl(""); setTipo("documento");
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 320px", gap: 14 }}>
      <Card padding={0}>
        <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cc-gray-200)" }}>
          <SectionTitle>Recursos compartidos ({materials.length})</SectionTitle>
        </div>
        {materials.length === 0 && <div style={{ padding: 18, fontSize: 13, color: "var(--cc-gray-500)" }}>Aún no hay material cargado.</div>}
        <div>
          {materials.map((m) => (
            <div key={m.id} style={{ padding: "12px 18px", borderTop: "1px solid var(--cc-gray-100)", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Badge tone="navy">{m.tipo}</Badge>
                  <a href={m.url} target="_blank" rel="noreferrer" style={{ fontSize: 13, fontWeight: 600, color: "var(--cc-navy)", textDecoration: "none" }}>{m.titulo}</a>
                </div>
                <div style={{ fontSize: 11, color: "var(--cc-gray-500)", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{m.url}</div>
                <div style={{ fontSize: 10, color: "var(--cc-gray-400)", marginTop: 2 }}>{new Date(m.creadoEn).toLocaleString("es-MX")}</div>
              </div>
              <button onClick={() => remove(grupoId, m.id)} data-testid={`del-mat-${m.id}`} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--cc-red)" }} aria-label="Eliminar">
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      </Card>

      <Card>
        <SectionTitle>Agregar material</SectionTitle>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <Field label="Título"><input data-testid="input-mat-titulo" value={titulo} onChange={(e) => setTitulo(e.target.value)} style={inp} /></Field>
          <Field label="URL"><input data-testid="input-mat-url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" style={inp} /></Field>
          <Field label="Tipo">
            <select data-testid="input-mat-tipo" value={tipo} onChange={(e) => setTipo(e.target.value as GroupMaterialTipo)} style={inp}>
              <option value="documento">Documento</option>
              <option value="video">Video</option>
              <option value="presentacion">Presentación</option>
              <option value="lectura">Lectura</option>
              <option value="enlace">Enlace</option>
            </select>
          </Field>
          <PrimaryButton onClick={submit} testId="btn-add-mat"><Plus size={14} /> Agregar</PrimaryButton>
        </div>
      </Card>
    </div>
  );
}

// ---------- Observaciones ----------
function ObservacionesTab({
  grupoId,
  candidatos,
  users,
}: {
  grupoId: string;
  candidatos: ReturnType<typeof useCandidatesList>;
  users: ReturnType<typeof useUsers>;
}) {
  const [openId, setOpenId] = useState<string | null>(candidatos[0]?.id ?? null);
  return (
    <div style={{ display: "grid", gridTemplateColumns: "260px 1fr", gap: 14 }}>
      <Card padding={0}>
        <div style={{ padding: "12px 14px", borderBottom: "1px solid var(--cc-gray-200)", fontSize: 13, fontWeight: 700 }}>Candidatos</div>
        {candidatos.map((c) => (
          <button
            key={c.id}
            onClick={() => setOpenId(c.id)}
            data-testid={`obs-cand-${c.id}`}
            style={{
              display: "block", width: "100%", textAlign: "left", padding: "10px 14px",
              background: openId === c.id ? "var(--cc-teal-bg)" : "transparent",
              border: "none", borderBottom: "1px solid var(--cc-gray-100)", cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 24, height: 24, borderRadius: "50%", background: c.avatarColor, color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, fontWeight: 700 }}>{c.iniciales}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: "var(--cc-navy)" }}>{c.nombre}</div>
            </div>
          </button>
        ))}
      </Card>

      {openId ? (
        <ObservationsCard candidatoId={openId} grupoId={grupoId} users={users} />
      ) : (
        <Card><div style={{ padding: 16 }}>Seleccione un candidato.</div></Card>
      )}
    </div>
  );
}

function ObservationsCard({ candidatoId, grupoId, users }: { candidatoId: string; grupoId: string; users: ReturnType<typeof useUsers> }) {
  const obs = useObservationsForCandidate(candidatoId);
  const add = useDemoStore((s) => s.addCandidateObservation);
  const remove = useDemoStore((s) => s.removeCandidateObservation);
  const me = useEffectiveUser();
  const [text, setText] = useState("");
  const submit = () => {
    const t = text.trim();
    if (!t) return;
    add({ candidatoId, grupoId, autorId: me?.id ?? "instructor", texto: t });
    setText("");
  };
  return (
    <Card padding={0}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--cc-gray-200)" }}>
        <SectionTitle>Observaciones ({obs.length})</SectionTitle>
        <div style={{ display: "flex", gap: 8 }}>
          <textarea
            data-testid="input-obs"
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={2}
            placeholder="Registrar una observación…"
            style={{ flex: 1, padding: 8, border: "1px solid var(--cc-gray-200)", borderRadius: 8, fontSize: 13, fontFamily: "inherit", resize: "vertical" }}
          />
          <PrimaryButton onClick={submit} testId="btn-add-obs"><Save size={14} /> Guardar</PrimaryButton>
        </div>
      </div>
      {obs.length === 0 && <div style={{ padding: 18, fontSize: 13, color: "var(--cc-gray-500)" }}>Sin observaciones registradas.</div>}
      <div>
        {obs.map((o) => (
          <div key={o.id} style={{ padding: "12px 18px", borderTop: "1px solid var(--cc-gray-100)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
              <div style={{ fontSize: 12, color: "var(--cc-gray-500)" }}>
                <UserCheck size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
                {users[o.autorId]?.nombre ?? o.autorId} · {new Date(o.ts).toLocaleString("es-MX")}
              </div>
              <button onClick={() => remove(candidatoId, o.id)} data-testid={`del-obs-${o.id}`} style={{ background: "transparent", border: "none", cursor: "pointer", color: "var(--cc-red)" }} aria-label="Eliminar">
                <Trash2 size={14} />
              </button>
            </div>
            <div style={{ fontSize: 13, color: "var(--cc-navy)", marginTop: 6, whiteSpace: "pre-wrap" }}>{o.texto}</div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: "var(--cc-gray-600)", textTransform: "uppercase" }}>{label}</span>
      {children}
    </label>
  );
}

const inp: React.CSSProperties = {
  width: "100%",
  padding: "8px 10px",
  border: "1px solid var(--cc-gray-200)",
  borderRadius: 6,
  fontSize: 13,
  fontFamily: "inherit",
};

// Silence unused warning while Link is in scope for cross-linking
void Link; void Users;
